import { format } from 'date-fns'
import { Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useExamples, useRemoveExample } from '@/hooks/use-examples'

interface ExamplesTabProps {
  phenotypeId: string
}

export function ExamplesTab({ phenotypeId }: ExamplesTabProps) {
  const { data: examples, isLoading, isError } = useExamples(phenotypeId)
  const { mutate: removeEx, isPending: isRemoving } = useRemoveExample()

  const handleRemove = (id: string, displayName: string) => {
    if (confirm(`Remove example "${displayName}" from this phenotype? This cannot be undone.`)) {
      removeEx({ id, phenotypeId })
    }
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Examples</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Examples</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Failed to load examples.</p>
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (!examples?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Examples (0)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No examples marked yet. Mark completed runs as examples from the Run Detail page.
          </p>
        </CardContent>
      </Card>
    )
  }

  // Examples list
  return (
    <Card>
      <CardHeader>
        <CardTitle>Examples ({examples.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {examples.map((ex) => (
            <div key={ex.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{ex.fileId}</p>
                <p className="text-xs text-muted-foreground">
                  Marked {format(new Date(ex.createdAt), 'MMM d, yyyy')}
                  {ex.markedBy && ` by ${ex.markedBy}`}
                  {' · '}
                  {ex.ratesCount} rates, {ex.surchargesCount} surcharges
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(ex.id, ex.fileId)}
                disabled={isRemoving}
                className="ml-2 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Remove example</span>
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
